import { JSX, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { BenchmarkingLineOfBusinessEnum } from "@app/overviews/network-benchmarking-dashboard/filters/shared/BenchmarkingLineOfBusinessEnum";
import { CompetitiveInsightsManageContext, getLineOfBusinessDisplayName, parseLineOfBusiness } from "@app/overviews/network-benchmarking-dashboard/manage/CompetitiveInsightsManageContext";
import { useCompetitiveInsightsManageAddModalClients } from "@app/overviews/network-benchmarking-dashboard/manage/hooks/useCompetitiveInsightsManageAddModalClients";
import { useCompetitiveInsightsManageAddModalConfigurationOptions } from "@app/overviews/network-benchmarking-dashboard/manage/hooks/useCompetitiveInsightsManageAddModalConfigurationOptions";
import { CompetitiveInsightsManageModalMode } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageModalMode";
import { CompetitiveInsightsManageProjectStatusEnum } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsManageProjectStatusEnum";
import { CompetitiveInsightsSupersetProjectConfigurations } from "@app/overviews/network-benchmarking-dashboard/manage/shared/CompetitiveInsightsSupersetProjectConfigurations";
import { SupersetProjectConfigurationRequestModel } from "@app/overviews/network-benchmarking-dashboard/manage/shared/SupersetProjectConfigurationRequestModel";
import BenchmarkingMedicaidService from "@app/overviews/network-benchmarking-dashboard/benchmarkingMedicaidService";
import BenchmarkingQHPService from "@app/overviews/network-benchmarking-dashboard/benchmarkingQHPService";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { UserContext } from "@app/user/UserContext";

const lineOfBusinessOptions: BenchmarkingLineOfBusinessEnum[] = Object.values(BenchmarkingLineOfBusinessEnum)
  .filter((value): value is BenchmarkingLineOfBusinessEnum => typeof value === "number" && value !== BenchmarkingLineOfBusinessEnum.Medicare);

interface CompetitiveInsightsManageAddModalBodyProps {
  configuration?: CompetitiveInsightsSupersetProjectConfigurations;
  mode: CompetitiveInsightsManageModalMode;
  onSubmitActionChange: (submitAction: () => void) => void;
  onValidityChange: (isValid: boolean) => void;
}

export const CompetitiveInsightsManageAddModalBody = (props: CompetitiveInsightsManageAddModalBodyProps): JSX.Element => {
  const userContext = useContext(UserContext);
  const isQuestInternal = userContext?.clientName === "Quest Analytics";
  const competitiveInsightsManageContext = useContext(CompetitiveInsightsManageContext);
  const isEdit = props.mode === "edit";
  const configuration = props.configuration;

  const [selectedLineOfBusiness, setSelectedLineOfBusiness] = useState<BenchmarkingLineOfBusinessEnum>(
    (configuration ? parseLineOfBusiness(configuration.lineOfBusiness) : undefined) ?? BenchmarkingLineOfBusinessEnum.Marketplace
  );
  const isMedicaid = selectedLineOfBusiness === BenchmarkingLineOfBusinessEnum.Medicaid;

  const [selectedClientId, setSelectedClientId] = useState<number | null>(configuration?.clientId ?? (isQuestInternal ? null : (userContext?.clientId ?? null)));
  const [selectedStateName, setSelectedStateName] = useState<string>(configuration?.stateName ?? "");
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(configuration?.projectId ?? null);
  const [selectedProgramKey, setSelectedProgramKey] = useState<string>(configuration?.program ?? "");
  const [selectedStatus, setSelectedStatus] = useState<CompetitiveInsightsManageProjectStatusEnum>(
    configuration?.isActive ? CompetitiveInsightsManageProjectStatusEnum.Active : CompetitiveInsightsManageProjectStatusEnum.Inactive
  );
  const [isInternalPreviewOnly, setIsInternalPreviewOnly] = useState<boolean>(configuration?.internalUserOnly ?? true);

  const benchmarkingMedicaidService = useMemo(() => new BenchmarkingMedicaidService(), []);
  const benchmarkingQHPService = useMemo(() => new BenchmarkingQHPService(), []);

  const { clients, isLoading: clientsLoading } = useCompetitiveInsightsManageAddModalClients(isQuestInternal);
  const { isLoading: addConfigurationOptionsLoading, programs, projects, states } = useCompetitiveInsightsManageAddModalConfigurationOptions(selectedClientId, isMedicaid);

  const isFetching = clientsLoading || addConfigurationOptionsLoading;

  const programsForState = programs.filter(program => program.stateName === selectedStateName);
  const projectsForState = projects.filter(project => project.states.includes(selectedStateName));
  const selectedStateId = states.find(state => state.stateName === selectedStateName)?.stateId;

  // The Client field has no "unavailable" state of its own: "Standard" (ClientId 0) is always a selectable
  // fallback for Quest-internal users, so it can never truly have zero options available.
  const stateOptionsUnavailable = selectedClientId !== null && !addConfigurationOptionsLoading && states.length === 0;
  const programOptionsUnavailable = isMedicaid && !!selectedStateName && !addConfigurationOptionsLoading && programsForState.length === 0;
  const projectOptionsUnavailable = !!selectedStateName && !addConfigurationOptionsLoading && projectsForState.length === 0;
  const anyOptionsUnavailable = stateOptionsUnavailable || programOptionsUnavailable || projectOptionsUnavailable;

  // A stored edit value can fall outside the options the API returns for the current client/state (e.g. a
  // client was deactivated, or a state/program/project was removed from the offering since this row was saved).
  // These flag that case so the retained value can be shown but not silently saved.
  const clientSelectionUnavailable = isQuestInternal && selectedClientId !== null && selectedClientId !== 0 && !clientsLoading && !clients.some(client => client.clientId === selectedClientId);
  const stateSelectionUnavailable = selectedClientId !== null && !!selectedStateName && !addConfigurationOptionsLoading && !states.some(state => state.stateName === selectedStateName);
  const programSelectionUnavailable = isMedicaid && !!selectedStateName && !!selectedProgramKey && !addConfigurationOptionsLoading && !programsForState.some(program => program.programKey === selectedProgramKey);
  const projectSelectionUnavailable = !!selectedStateName && !!selectedProjectId && !addConfigurationOptionsLoading && !projectsForState.some(project => project.projectId === selectedProjectId);
  const anySelectionUnavailable = clientSelectionUnavailable || stateSelectionUnavailable || programSelectionUnavailable || projectSelectionUnavailable;

  const renderOptionsField = (params: { emptyMessage: string; hasOptions: boolean; isReady: boolean; loading: boolean; select: JSX.Element }): ReactNode => {
    if (params.isReady && params.loading) {
      return (
        <div className="mb-2 text-center">
          <LoadingSpinner />
        </div>
      );
    }

    if (params.isReady && !params.hasOptions) {
      return (
        <label className="col-form-label text-start fw-normal mb-2">
          <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
          {params.emptyMessage}
        </label>
      );
    }

    return params.select;
  }

  const onLineOfBusinessChange = (lineOfBusiness: BenchmarkingLineOfBusinessEnum): void => {
    setSelectedLineOfBusiness(lineOfBusiness);
    setSelectedStateName("");
    setSelectedProjectId(null);
    setSelectedProgramKey("");
  }

  const onClientChange = (clientId: number | null): void => {
    setSelectedClientId(clientId);
    setSelectedStateName("");
    setSelectedProjectId(null);
    setSelectedProgramKey("");
  }

  const onStateChange = (stateName: string): void => {
    setSelectedStateName(stateName);
    setSelectedProjectId(null);
    setSelectedProgramKey("");
  }

  useEffect(() => {
    const isValid = selectedClientId !== null
      && !!selectedStateName
      && !!selectedProjectId
      && (!isMedicaid || !!selectedProgramKey)
      && !anyOptionsUnavailable
      && !anySelectionUnavailable;

    props.onValidityChange(isValid);
  }, [selectedClientId, selectedStateName, selectedProjectId, selectedProgramKey, isMedicaid, anyOptionsUnavailable, anySelectionUnavailable, props.onValidityChange]);

  const { mutate: saveConfiguration } = useMutation({
    mutationFn: async (): Promise<void> => {
      if (isEdit && !configuration) {
        return;
      }

      const payload: SupersetProjectConfigurationRequestModel = {
        clientId: selectedClientId ?? 0,
        internalUserOnly: isInternalPreviewOnly,
        isActive: selectedStatus === CompetitiveInsightsManageProjectStatusEnum.Active,
        originalClientId: isEdit ? (configuration?.clientId ?? 0) : 0,
        originalProgram: isEdit ? (configuration?.program ?? "") : "",
        originalStateId: isEdit ? (configuration?.stateId ?? 0) : 0,
        program: isMedicaid ? selectedProgramKey : "",
        projectId: selectedProjectId ?? 0,
        stateId: selectedStateId ?? 0,
        templateId: 0,
      };

      if (isEdit) {
        if (isMedicaid) {
          await benchmarkingMedicaidService.editSupersetProjectConfiguration(payload);
        } else {
          await benchmarkingQHPService.editSupersetProjectConfiguration(payload);
        }
      } else {
        if (isMedicaid) {
          await benchmarkingMedicaidService.addSupersetProjectConfiguration(payload);
        } else {
          await benchmarkingQHPService.addSupersetProjectConfiguration(payload);
        }
      }
    },
    onError: (e: unknown) => {
      const status = e !== null && typeof e === "object" && "status" in e ? (e as { status: unknown }).status : undefined;
      toast.error(
        status === 409
          ? "A configuration with this State, Client, and Program combination already exists."
          : "Unable to save the configuration. Please try again.",
        { closeOnClick: true, containerId: "z-index-9999" }
      );
    },
    onSuccess: async () => {
      // the save has already succeeded here, so a failed list refresh must not reach onError and read as a failed save
      try {
        await competitiveInsightsManageContext.refetchProjects();
      } catch {
        toast.warning("The configuration was saved, but the project list could not be refreshed. Please reload the page.", { closeOnClick: true, containerId: "z-index-9999" });
      }
    },
  });

  useEffect(() => {
    props.onSubmitActionChange(() => saveConfiguration());
  }, [saveConfiguration, props.onSubmitActionChange]);

  return (
    <div>
      <QESSelect
        containerClassName="mb-2"
        disabled={isEdit}
        isValid={!!selectedLineOfBusiness}
        label="Line of Business"
        onChange={(e) => onLineOfBusinessChange(Number(e.target.value) as BenchmarkingLineOfBusinessEnum)}
        required={true}
        testId="competitive-insights__manage--add-modal-line-of-business-select"
        value={selectedLineOfBusiness}
      >
        {lineOfBusinessOptions.map(lineOfBusiness => (
          <option key={lineOfBusiness} value={lineOfBusiness}>{getLineOfBusinessDisplayName(lineOfBusiness)}</option>
        ))}
      </QESSelect>

      {isQuestInternal && renderOptionsField({
        emptyMessage: "No client options available.",
        hasOptions: true,
        isReady: true,
        loading: clientsLoading,
        select: (
          <QESSelect
            containerClassName="mb-2"
            disabled={isFetching}
            isValid={selectedClientId !== null && !clientSelectionUnavailable}
            label="Client"
            onChange={(e) => onClientChange(e.target.value ? Number(e.target.value) : null)}
            required={true}
            testId="competitive-insights__manage--add-modal-client-select"
            value={selectedClientId ?? ""}
          >
            <option key="0" value="" disabled={true}> </option>
            <option key="standard" value={0}>Standard</option>
            {clientSelectionUnavailable && (
              <option key="retained" value={selectedClientId ?? ""} disabled={true}>{`${configuration?.clientName ?? selectedClientId} (unavailable)`}</option>
            )}
            {clients.map(client => (
              <option key={client.clientId} value={client.clientId}>{client.name}</option>
            ))}
          </QESSelect>
        ),
      })}

      {renderOptionsField({
        emptyMessage: "No state options available.",
        hasOptions: states.length > 0,
        isReady: selectedClientId !== null,
        loading: addConfigurationOptionsLoading,
        select: (
          <QESSelect
            containerClassName="mb-2"
            disabled={isFetching || selectedClientId === null}
            isValid={!!selectedStateName && !stateSelectionUnavailable}
            label="State"
            onChange={(e) => onStateChange(e.target.value)}
            required={true}
            testId="competitive-insights__manage--add-modal-state-select"
            value={selectedStateName}
          >
            <option key="0" value="" disabled={true}> </option>
            {stateSelectionUnavailable && (
              <option key="retained" value={selectedStateName} disabled={true}>{`${selectedStateName} (unavailable)`}</option>
            )}
            {states.map(state => (
              <option key={state.stateId} value={state.stateName}>{state.stateName}</option>
            ))}
          </QESSelect>
        ),
      })}

      {isMedicaid && renderOptionsField({
        emptyMessage: "No program options available.",
        hasOptions: programsForState.length > 0,
        isReady: !!selectedStateName,
        loading: addConfigurationOptionsLoading,
        select: (
          <QESSelect
            containerClassName="mb-2"
            disabled={isFetching || !selectedStateName}
            isValid={!!selectedProgramKey && !programSelectionUnavailable}
            label="Program"
            onChange={(e) => setSelectedProgramKey(e.target.value)}
            required={true}
            testId="competitive-insights__manage--add-modal-program-select"
            value={selectedProgramKey}
          >
            <option key="0" value="" disabled={true}> </option>
            {programSelectionUnavailable && (
              <option key="retained" value={selectedProgramKey} disabled={true}>{`${selectedProgramKey} (unavailable)`}</option>
            )}
            {programsForState.map(program => (
              <option key={program.programKey} value={program.programKey}>{program.programName}</option>
            ))}
          </QESSelect>
        ),
      })}

      {renderOptionsField({
        emptyMessage: "No project options available.",
        hasOptions: projectsForState.length > 0,
        isReady: !!selectedStateName,
        loading: addConfigurationOptionsLoading,
        select: (
          <QESSelect
            containerClassName="mb-2"
            disabled={isFetching || !selectedStateName}
            isValid={!!selectedProjectId && !projectSelectionUnavailable}
            label="Project"
            onChange={(e) => setSelectedProjectId(e.target.value ? Number(e.target.value) : null)}
            required={true}
            testId="competitive-insights__manage--add-modal-project-select"
            value={selectedProjectId ?? ""}
          >
            <option key="0" value="" disabled={true}> </option>
            {projectSelectionUnavailable && (
              <option key="retained" value={selectedProjectId ?? ""} disabled={true}>{`${configuration?.projectName ?? selectedProjectId} (unavailable)`}</option>
            )}
            {projectsForState.map(project => (
              <option key={project.projectId} value={project.projectId}>{project.name}</option>
            ))}
          </QESSelect>
        ),
      })}

      <QESSelect
        containerClassName="mb-2"
        label="Status"
        onChange={(e) => setSelectedStatus(e.target.value as CompetitiveInsightsManageProjectStatusEnum)}
        testId="competitive-insights__manage--add-modal-status-select"
        value={selectedStatus}
      >
        {Object.values(CompetitiveInsightsManageProjectStatusEnum).map(status => (
          <option key={status} value={status}>{status}</option>
        ))}
      </QESSelect>

      <QESSelect
        containerClassName="mb-2"
        label="Internal Preview Only"
        onChange={(e) => setIsInternalPreviewOnly(e.target.value === "true")}
        testId="competitive-insights__manage--add-modal-internal-preview-only-select"
        value={String(isInternalPreviewOnly)}
      >
        <option value="false">No</option>
        <option value="true">Yes</option>
      </QESSelect>
    </div>
  );
}

export default CompetitiveInsightsManageAddModalBody;
